/**
 * computeTopAudiences.js
 * ---------------------------------------------------------------------------
 * Report-friendly extraction of MapPage's "Compute top audience" logic
 * (see utils/mapPage/fetchAudienceInsight.js). For a question (a group of
 * response options) within an area, it finds - per option - the demographic
 * sub-groups with the highest weighted-average support.
 *
 * Two-stage scan, mirroring the map:
 *   1. best single value per demographic dimension (gender/age/education/…)
 *   2. best combined pair drawn from the top-3 scoring dimensions
 *
 * IMPORTANT - scale correctness:
 *   The response value must be on the SAME scale as the chart. Many questions
 *   set `normalise_over_options: true`, which the backend applies row-wise
 *   across the *full* option set. So we must (a) send the full `groupKeys`,
 *   and (b) pass the group's real `normaliseOverOptions` flag - otherwise a
 *   filtered value comes back un-normalised (e.g. 11% instead of ~40%).
 *   Sending a single option key would also break normalisation (a lone column
 *   rescales to 100%). Fetching all options at once additionally lets one set
 *   of filtered requests score every option, so this is cheaper too.
 */
import { tableFromIPC } from "apache-arrow";
import { DEMO_LABELS } from "./demographicLabels";
import { schemaRequestFields } from "./schemaTypes";
import { weightColForKey, weightFromRowByCol, weightedMeanByKey } from "./keyWeights";

// Demographic dimensions and the values probed for each (same set the map uses).
const CANDIDATES = {
  gender: [1, 2],
  age: [1, 2, 3],
  education: [1, 2],
  ethnicity: [1, 2],
  party: [1, 2, 3, 4, 5, 6, 7, 8, 9],
};

function rowsFromTable(table) {
  return table.toArray().map((r) => r.toJSON());
}

/**
 * Weighted national value per key from per-constituency rows (already one
 * latest-wave row per area, e.g. from fetchConstituencyGroup). Returns
 * { [key]: weightedPct }. This is the SAME basis computeTopAudiences uses for
 * subgroups, so the overall response % and the top-audience %s are comparable.
 *
 * Each key is weighted by the column the backend divided by — n_voters for
 * vote-share questions, n_raked otherwise. See utils/keyWeights.js.
 */
export function weightedNationalByKey(rows, keys) {
  return weightedMeanByKey(rows, keys);
}

async function postArrow({ level, body, signal }) {
  const res = await fetch(`/api/${level}_data_arrow/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) return null;
  return rowsFromTable(tableFromIPC(await res.arrayBuffer()));
}

/**
 * @returns {Object} audiencesByOption - { [optionKey]: { perDimension, topPairs, combined } }
 */
export async function computeTopAudiences({
  areaCode,
  groupKeys,                    // FULL set of column keys to request (for normalisation)
  options,                      // [{ key, sumKeys }] - each profiled option sums its sumKeys
  level = "constituency",
  theme = "pol",
  schema = "tracking",
  normaliseOverOptions = false,
  waves = null,                 // group.waves - restricts which waves are valid
  national = false,             // true → score across all of GB (areaCodes: [])
  signal,
}) {
  const allKeys = (groupKeys || []).filter(Boolean);
  // National scope aggregates the constituency dataset on the client: there is
  // no standalone national parquet locally, and the national endpoint's average
  // is UNWEIGHTED. We instead take an n_raked-weighted mean across constituencies
  // at the latest wave (see valueOf) — the same basis the overall response % must
  // use (weightedNationalByKey), so a genuine "top" subgroup is always ≥ overall.
  // Each option identifies a value as the sum of one or more response columns.
  // A plain categorical option sums a single key; a likert-grid sector sums its
  // positive-response columns.
  const profile = (options || [])
    .map((o) => ({ key: o.key, sumKeys: (o.sumKeys || [o.key]).filter(Boolean) }))
    .filter((o) => o.sumKeys.length);
  if (!allKeys.length || (!national && !areaCode) || !profile.length) return {};

  const baseBody = {
    areaCodes: national ? [] : [String(areaCode)],
    theme,
    groupKeys: allKeys,
    filters: {},
    ...schemaRequestFields(schema),
    // Per-wave rows (NOT pooled). Many questions only ran in specific waves;
    // pooling would put a single-wave numerator over an all-wave denominator
    // and dilute every value. We pick the latest valid wave instead - matching
    // loadGroupData's pickLatestWave so audience values share the chart's basis.
    group_by_wave: true,
    normalise_over_options: !!normaliseOverOptions,
  };

  const labelFor = (dim, val) => DEMO_LABELS?.[dim]?.[val] || String(val);
  const dimLabel = (dim) => dim.charAt(0).toUpperCase() + dim.slice(1);

  const allowedWaves = Array.isArray(waves) && waves.length
    ? new Set(waves.map(Number))
    : null;

  // Pick the latest valid wave row for the area (mirrors metricsClient.loadGroupData).
  const pickLatestWave = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    if (!("Wave" in (rows[0] || {}))) return rows[0];
    const candidates = allowedWaves
      ? rows.filter((r) => allowedWaves.has(Number(r.Wave)))
      : rows;
    if (!candidates.length) return null;
    const maxW = Math.max(...candidates.map((r) => Number(r.Wave) || 0));
    return candidates.find((r) => Number(r.Wave) === maxW) || candidates[0];
  };

  const sumRow = (row, sumKeys) => {
    let acc = 0, seen = false;
    for (const k of sumKeys) {
      const v = Number(row?.[k]);
      if (Number.isFinite(v)) { acc += v; seen = true; }
    }
    return seen ? acc : null;
  };

  // Read an option's value. Constituency scope: the latest-wave row for the one
  // area. National scope: the weighted mean across all constituencies at the
  // latest wave — Σ(w·rate)/Σ(w) — which equals the true GB weighted rate.
  // An option sums keys from one group, so they share a weight column.
  // Because demographic dimensions partition the population, this guarantees the
  // top subgroup for an option is never below that option's overall national %.
  const valueOf = (rows, sumKeys) => {
    if (!Array.isArray(rows) || !rows.length) return null;
    if (!national) return sumRow(pickLatestWave(rows), sumKeys);
    const cand = allowedWaves ? rows.filter((r) => allowedWaves.has(Number(r.Wave))) : rows;
    if (!cand.length) return null;
    const maxW = Math.max(...cand.map((r) => Number(r.Wave) || 0));
    const weightCol = weightColForKey(sumKeys[0]);
    let num = 0, den = 0;
    for (const r of cand) {
      if ((Number(r.Wave) || 0) !== maxW) continue;
      const v = sumRow(r, sumKeys);
      if (!Number.isFinite(v)) continue;
      const w = weightFromRowByCol(r, weightCol) ?? 1;
      num += w * v;
      den += w;
    }
    return den > 0 ? num / den : null;
  };
  const fetchRow = (filters) =>
    postArrow({ level, signal, body: { ...baseBody, filters } }).catch(() => null);

  // ── Step 1: single-dimension matrix (each request returns ALL options) ─────
  const singleDefs = [];
  for (const [dim, values] of Object.entries(CANDIDATES)) {
    for (const val of values) singleDefs.push({ dim, val });
  }
  const singleRows = await Promise.all(
    singleDefs.map((d) => fetchRow({ [d.dim]: d.val }).then((rows) => ({ ...d, rows })))
  );

  // Best single value per (option, dimension)
  const bestByOptionDim = {}; // optKey -> dim -> { dim, val, score }
  for (const opt of profile) {
    const bestByDim = {};
    for (const s of singleRows) {
      const score = valueOf(s.rows, opt.sumKeys);
      if (!Number.isFinite(score)) continue;
      if (!bestByDim[s.dim] || score > bestByDim[s.dim].score) {
        bestByDim[s.dim] = { dim: s.dim, val: s.val, score };
      }
    }
    bestByOptionDim[opt.key] = bestByDim;
  }

  // ── Step 2: union of pair-filters from each option's top-3 dimensions ──────
  const pairSig = (a, b) => `${a.dim}:${a.val}|${b.dim}:${b.val}`;
  const topDimsFor = (optKey) =>
    Object.values(bestByOptionDim[optKey]).sort((a, b) => b.score - a.score).slice(0, 3);

  const pairNeeded = new Map(); // sig -> { dimA, valA, dimB, valB }
  for (const opt of profile) {
    const td = topDimsFor(opt.key);
    for (let i = 0; i < td.length; i++) {
      for (let j = i + 1; j < td.length; j++) {
        const A = td[i], B = td[j];
        const sig = pairSig(A, B);
        if (!pairNeeded.has(sig)) {
          pairNeeded.set(sig, { dimA: A.dim, valA: A.val, dimB: B.dim, valB: B.val });
        }
      }
    }
  }
  const pairEntries = Array.from(pairNeeded.entries());
  const pairResults = await Promise.all(
    pairEntries.map(([sig, p]) =>
      fetchRow({ [p.dimA]: p.valA, [p.dimB]: p.valB }).then((rows) => [sig, rows])
    )
  );
  const pairRowMap = new Map(pairResults);

  // ── Assemble per-option output ─────────────────────────────────────────────
  const audiencesByOption = {};
  for (const opt of profile) {
    const bestByDim = bestByOptionDim[opt.key];
    const perDimension = Object.fromEntries(
      Object.entries(bestByDim).map(([dim, info]) => [
        dim,
        { dimension: dimLabel(dim), label: labelFor(dim, info.val), score: info.score },
      ])
    );

    const td = topDimsFor(opt.key);
    const optPairs = [];
    for (let i = 0; i < td.length; i++) {
      for (let j = i + 1; j < td.length; j++) {
        const A = td[i], B = td[j];
        const score = valueOf(pairRowMap.get(pairSig(A, B)), opt.sumKeys);
        if (Number.isFinite(score)) {
          optPairs.push({
            label: `${labelFor(A.dim, A.val)} + ${labelFor(B.dim, B.val)}`,
            dimension: `${dimLabel(A.dim)} + ${dimLabel(B.dim)}`,
            score,
          });
        }
      }
    }
    optPairs.sort((a, b) => b.score - a.score);

    const bestSingle = td[0] || null;
    const bestPair = optPairs[0] || null;
    // "Strongest audience" = the highest-scoring segment, single OR pair.
    // (Intersecting two constraints can lower support, so we don't force a pair.)
    let combined = null;
    if (bestPair && (!bestSingle || bestPair.score >= bestSingle.score)) {
      combined = { dimension: bestPair.dimension, label: bestPair.label, score: bestPair.score };
    } else if (bestSingle) {
      combined = {
        dimension: dimLabel(bestSingle.dim),
        label: labelFor(bestSingle.dim, bestSingle.val),
        score: bestSingle.score,
      };
    }

    audiencesByOption[opt.key] = { perDimension, topPairs: optPairs.slice(0, 3), combined };
  }

  return audiencesByOption;
}
